import { skipToken, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type AtomEffect, atom, selectorFamily, useRecoilValue, useSetRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Button, toast } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { useNavigationState } from '../../../navigation';
import { apiQuery, getInitialDataForChannel, subscribeToRealtimeChannel } from '../../../state';
import { persistRecoilEffect } from '../../../utils';

type ImportOrigin = api.SitePointer;

interface ImportTargetFilter {
    space: string;
    /**
     * If `null`, only targets without a change request will match.
     */
    changeRequest?: string | null | undefined;
    /**
     * If `null`, only targets without a page will match.
     */
    page?: string | null | undefined;
}

export interface ImportRunTrace {
    id: string;
    origin: ImportOrigin | null;
    target: api.ContentImportTarget;
    timestamp: string;
}

const garbageCollectImportRuns: AtomEffect<ImportRunTrace[]> = (args) => {
    if (args.trigger === 'get') {
        args.onSet((value) => {
            // Keep only the last 24h of import runs
            const filtered = value.filter((run) => {
                return new Date(run.timestamp).getTime() > Date.now() - 1000 * 60 * 60 * 24;
            });
            if (filtered.length !== value.length) {
                args.setSelf(filtered);
            }
        });
    }
};

const allImportRuns = atom<ImportRunTrace[]>({
    key: 'importRun',
    default: [],
    effects: [
        persistRecoilEffect({
            // We need to init async because we are garbage collecting synchronously when we set
            initAsync: true,
        }),
        garbageCollectImportRuns,
    ],
});

/**
 * Hook to persist an import run.
 */
export function usePersistImportRun() {
    const setSate = useSetRecoilState(allImportRuns);
    return useCallback(
        (data: Omit<ImportRunTrace, 'timestamp'>) => {
            setSate((prev) => [{ ...data, timestamp: new Date().toISOString() }, ...prev]);
        },
        [setSate]
    );
}

const importRunsPerTarget = selectorFamily<ImportRunTrace[], Readonly<ImportTargetFilter>>({
    key: 'importRuns-target',
    get:
        (target: ImportTargetFilter) =>
        ({ get }) => {
            return get(allImportRuns).filter((run) => checkIsTargetMatchFilter(run.target, target));
        },
});

const importRunsPerSite = selectorFamily<ImportRunTrace[], string>({
    key: 'importRuns-site',
    get:
        (siteId: string) =>
        ({ get }) => {
            return get(allImportRuns).filter(
                (run) => run.origin?.type === 'site' && run.origin.site === siteId
            );
        },
});

/**
 * Hook to get the import run traces for a given target.
 */
function useImportRunTraces(props?: { target?: ImportTargetFilter }) {
    return useRecoilValue(props?.target ? importRunsPerTarget(props.target) : allImportRuns);
}

export interface ImportRunWithTrace extends api.ContentImportRun {
    trace: ImportRunTrace;
}

/**
 * Helper to check if two targets are equal.
 */
function checkIsTargetMatchFilter(
    target: api.ContentImportTarget,
    filter: ImportTargetFilter
): boolean {
    if (target.space !== filter.space) {
        return false;
    }
    if (filter.changeRequest !== undefined) {
        if ((target.changeRequest ?? null) !== filter.changeRequest) {
            return false;
        }
    }
    if (filter.page !== undefined) {
        if ((target.page ?? null) !== filter.page) {
            return false;
        }
    }
    return true;
}

function useImportRunsFromTraces(traces: ImportRunTrace[]): ImportRunWithTrace[] {
    const [data, setData] = useState<{ [key: string]: api.ContentImportRun }>(() => {
        return traces.reduce<{ [key: string]: api.ContentImportRun }>((acc, run) => {
            const data = getInitialDataForChannel({ channel: 'import-run', runId: run.id });
            if (data) {
                acc[run.id] = data;
            }
            return acc;
        }, {});
    });
    useEffect(() => {
        const subscriptions = traces.map((run) => {
            return subscribeToRealtimeChannel(
                {
                    channel: 'import-run',
                    runId: run.id,
                },
                (data) => {
                    setData((existing) => ({
                        ...existing,
                        [run.id]: data,
                    }));
                }
            );
        });
        return () => {
            subscriptions.forEach((unsub) => unsub());
        };
    }, [traces]);
    return useMemo(
        () =>
            traces
                .map((trace) => (data[trace.id] ? { ...data[trace.id], trace } : undefined))
                .filter((x) => x !== undefined),
        [data, traces]
    );
}

/**
 * Hook to get the import runs for a given site, with realtime updates.
 */
export function useSiteImportRuns(siteId: string): ImportRunWithTrace[] {
    const traces = useRecoilValue(importRunsPerSite(siteId));
    return useImportRunsFromTraces(traces);
}

/**
 * Hook to get the import runs for a given target, with realtime updates.
 */
export function useImportRuns(props?: { target?: ImportTargetFilter }): ImportRunWithTrace[] {
    const traces = useImportRunTraces(props);
    return useImportRunsFromTraces(traces);
}

/**
 * Hook to get the source URL of an imported page, if any.
 */
export function usePageImportSourceURL(props: {
    target: Omit<ImportTargetFilter, 'page'>;
    pageId: string;
}): string | null {
    const importRuns = useImportRuns(props);
    return useMemo(() => {
        for (const run of importRuns) {
            const page = run.pages?.find((page) => page.id === props.pageId);
            if (page?.sourceURL) {
                return page.sourceURL;
            }
        }
        return null;
    }, [importRuns, props.pageId]);
}

/**
 * Resolvable promise.
 */
type Resolvable<T> = {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (error: Error) => void;
};

/**
 * Create a resolvable promise.
 */
function createResolvable<T>(): Resolvable<T> {
    let resolve: (value: T) => void;
    let reject: (error: Error) => void;
    const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    // biome-ignore lint/style/noNonNullAssertion: explicitly assigned
    return { promise, resolve: resolve!, reject: reject! };
}

/**
 * Component to show toasts for import runs.
 */
export function ImportRunToasts() {
    const { screen } = useNavigationState();
    const runs = useImportRuns();
    const resolvablesRef = useRef<Record<string, Resolvable<void> | undefined>>({});

    const { data: changeRequest } = useQuery(
        apiQuery(
            'getChangeRequestById',
            screen.type === 'space' && screen.changeRequest && runs.length > 0
                ? {
                      spaceId: screen.space,
                      changeRequestId: String(screen.changeRequest),
                  }
                : skipToken
        )
    );

    const screenSpaceId = screen.type === 'space' ? screen.space : null;
    const screenChangeRequestId = changeRequest?.id;
    const activeTarget = useMemo(
        (): ImportTargetFilter | null =>
            screenSpaceId
                ? {
                      space: screenSpaceId,
                      changeRequest: screenChangeRequestId ?? null,
                  }
                : null,
        [screenSpaceId, screenChangeRequestId]
    );

    useEffect(() => {
        for (const run of runs) {
            const isSiteImport = run.trace.origin?.type === 'site';
            if (isSiteImport) {
                continue;
            }

            const isForActiveTarget =
                activeTarget && checkIsTargetMatchFilter(run.trace.target, activeTarget);

            if (resolvablesRef.current[run.id]) {
                switch (run.status) {
                    case 'cancelled':
                    case 'failed': {
                        resolvablesRef.current[run.id]?.reject(
                            new Error('Import failed or was cancelled')
                        );
                        delete resolvablesRef.current[run.id];
                        break;
                    }
                    case 'completed': {
                        resolvablesRef.current[run.id]?.resolve();
                        delete resolvablesRef.current[run.id];
                        break;
                    }
                    case 'in-progress':
                    case 'pending': {
                        // If we are on the active target, we can dismiss the toast.
                        if (isForActiveTarget) {
                            toast.dismiss(`import-run-${run.id}`);
                            delete resolvablesRef.current[run.id];
                        }
                        break;
                    }
                    default:
                        assertNever(run.status);
                }
            } else if (
                !isForActiveTarget &&
                (run.status === 'pending' || run.status === 'in-progress')
            ) {
                const resolvable = createResolvable<void>() as Resolvable<void>;
                resolvablesRef.current[run.id] = resolvable;
                toast.promise(resolvable.promise, {
                    id: `import-run-${run.id}`,
                    loading: `Importing content…`,
                    success: 'Import completed',
                    error: 'Error while importing your content',
                    closeButton: false,
                    action: (
                        <Button
                            kind="secondary"
                            label={
                                run.trace.target.changeRequest
                                    ? 'View change request'
                                    : 'View space'
                            }
                            href={{
                                current: {
                                    type: 'space',
                                    space: run.trace.target.space,
                                    page: '',
                                    changeRequest: run.trace.target.changeRequest,
                                },
                            }}
                            onPress={() => {
                                analytics.logEvent({
                                    type: 'import_open',
                                    trigger: 'toast',
                                    status: run.status,
                                });
                            }}
                        />
                    ),
                });
            }
        }
    }, [runs, activeTarget]);

    return null;
}
